/**
 * FleeFromTargetAction - behavior tree action for fleeing from threats.
 */

import * as THREE from 'three';

import { NpcMovementComponent } from '../../../../../components/movement/NpcMovementComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Actor } from '../../../../../actors/Actor.js';
import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface FleeFromTargetActionOptions extends BehaviorActionOptions {
  targetActorKey?: string;
  targetPositionKey?: string;
  fleeDistance?: number;
  maxFleeDistance?: number;
  fleeSpeedMultiplier?: number;
  useNavigation?: boolean;
}

@EngineClass('FleeFromTargetAction')
export class FleeFromTargetAction extends BehaviorAction {
  private targetActorKey?: string;
  private targetPositionKey?: string;
  private fleeDistance: number;
  private movementComponent: NpcMovementComponent | null = null;

  constructor(options: FleeFromTargetActionOptions = {}) {
    super({
      name: 'FleeFromTarget',
      ...options
    });

    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
    this.targetPositionKey = options.targetPositionKey;
    this.fleeDistance = options.fleeDistance ?? 15.0;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    this.movementComponent = owner.getComponent(NpcMovementComponent);
    if (!this.movementComponent) {
      console.warn('FleeFromTargetAction: No NpcMovementComponent found on actor');
      return;
    }
  }

  protected override onEnter(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent) {
      return;
    }

    // Get threat position
    const threatPos = this.getThreatPosition(blackboard);
    if (!threatPos) {
      console.warn('FleeFromTargetAction: No threat position found');
      return;
    }

    // Calculate flee position and use movement component to handle pathfinding
    const fleePosition = this.calculateFleePosition(owner, threatPos);
    this.movementComponent.setTargetPosition(fleePosition);
  }

  protected override async onUpdate(blackboard: Blackboard): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent) {
      return BehaviorStatus.Failure;
    }

    // Get current threat position
    const threatPos = this.getThreatPosition(blackboard);
    if (!threatPos) {
      return BehaviorStatus.Failure;
    }

    // Get current position
    const ownerPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);

    // Calculate distance to threat
    const distanceToThreat = ownerPosition.distanceTo(threatPos);

    // Check if we're far enough away
    if (distanceToThreat >= this.fleeDistance) {
      // We're safe, stop fleeing
      this.movementComponent.stop();
      return BehaviorStatus.Success;
    }

    // Continue fleeing if we haven't reached our destination
    if (!this.movementComponent.hasReachedCurrentTarget()) {
      return BehaviorStatus.Running;
    }

    // We reached our flee point but we're still too close, flee further
    const newFleePosition = this.calculateFleePosition(owner, threatPos);
    this.movementComponent.setTargetPosition(newFleePosition);

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    // Stop movement
    this.movementComponent?.stopMovement();
  }

  private getThreatPosition(blackboard: Blackboard): THREE.Vector3 | null {
    if (this.targetActorKey) {
      const actor = blackboard.getValue<Actor>(this.targetActorKey);
      if (actor) {
        const position = new THREE.Vector3();
        actor.rootComponent.getWorldPosition(position);
        return position;
      }
    }

    if (this.targetPositionKey) {
      const position = blackboard.getValue<THREE.Vector3>(this.targetPositionKey);
      if (position) {
        return position.clone();
      }
    }

    return null;
  }

  private calculateFleePosition(owner: Actor, threatPos: THREE.Vector3): THREE.Vector3 {
    // Get current position
    const ownerPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);

    // Calculate flee direction (away from threat)
    const fleeDirection = ownerPosition.clone().sub(threatPos);
    fleeDirection.y = 0; // Keep on same height level
    fleeDirection.normalize();

    // Calculate flee target position
    const fleeTarget = ownerPosition.clone().add(fleeDirection.multiplyScalar(this.fleeDistance));

    return fleeTarget;
  }
}
